import { useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { cn } from "@/utils/helpers";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { MONTH_NAMES, sixWeekGrid, type CalendarPost } from "./calendarModel";
import type { MonthPickerData } from "./CalendarHeader";

const HEADS = ["S", "M", "T", "W", "T", "F", "S"];
const glass44 =
  "co-glass-control grid size-11 shrink-0 place-items-center rounded-pill text-co-text2 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent";

/**
 * Calendar / Month picker · Phone: what "December ▾" opens on a phone. A
 * floating glass sheet under the top lane (8 in, radius 16) over a scrim: the
 * month with its year capsule (to the year) and arrows, the days with their
 * dots, then "Tap a day to jump the list" and Today.
 */
export default function MonthPickerSheet({
  picker,
  posts = [],
  chosenDay = null,
  onPickDay,
  onClose,
}: {
  picker: MonthPickerData;
  /** The open month's posts, for its dots. Other months show their dates only. */
  posts?: readonly CalendarPost[];
  /** The open month's chosen day, lit. */
  chosenDay?: number | null;
  /** A day of the open month: jump the list to it. */
  onPickDay?: (day: number) => void;
  onClose: () => void;
}) {
  const portal = useCoShellPortal();
  const [at, setAt] = useState({ year: picker.year, month: picker.month });
  const step = (delta: number) =>
    setAt((current) => {
      const index = current.year * 12 + (current.month - 1) + delta;
      return { year: Math.floor(index / 12), month: (index % 12) + 1 };
    });
  const isOpenMonth = at.year === picker.year && at.month === picker.month;
  const cells = sixWeekGrid(at.year, at.month);
  const weeks = Math.ceil((cells.map((cell) => cell.inMonth).lastIndexOf(true) + 1) / 7);
  const byDay = new Map<string, CalendarPost[]>();
  if (isOpenMonth) {
    for (const post of posts) {
      const key = post.slot.scheduledAt.slice(0, 10);
      byDay.set(key, [...(byDay.get(key) ?? []), post]);
    }
  }
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const pick = (day: number) => {
    onClose();
    if (isOpenMonth && onPickDay) onPickDay(day);
    else picker.onPick(at.year, at.month);
  };

  return (
    <ModalOverlay
      isOpen
      isDismissable
      onOpenChange={(open) => !open && onClose()}
      UNSTABLE_portalContainer={portal ?? undefined}
      className="fixed inset-0 z-[var(--z-index-modal)] bg-co-scrim"
    >
      <Modal className="fixed inset-x-2 top-16 outline-none">
        <Dialog
          aria-label="Choose a day"
          className="co-glass-chrome flex flex-col gap-2.5 rounded-panel px-3.5 pb-4 pt-3.5 outline-none"
        >
          <div className="flex items-center gap-1.5">
            <h2 className="min-w-0 flex-1 truncate font-section text-[20px] font-bold text-co-text">
              {MONTH_NAMES[at.month - 1]} {at.year}
            </h2>
            {picker.onSeeYear ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  picker.onSeeYear?.();
                }}
                className="co-glass-control inline-flex h-8 shrink-0 items-center gap-1 rounded-pill px-2.5 text-[13px] font-medium text-co-text outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
              >
                {at.year}
                <ArrowUpRight className="size-[13px] text-co-text2" aria-hidden="true" />
              </button>
            ) : null}
            <button type="button" aria-label="Previous month" onClick={() => step(-1)} className={glass44}>
              <ChevronLeft className="size-[18px]" aria-hidden="true" />
            </button>
            <button type="button" aria-label="Next month" onClick={() => step(1)} className={glass44}>
              <ChevronRight className="size-[18px]" aria-hidden="true" />
            </button>
          </div>
          <div role="grid" aria-label={`${MONTH_NAMES[at.month - 1]} ${at.year}`} className="grid grid-cols-7">
            <div role="row" className="contents">
              {HEADS.map((head, index) => (
                <span role="columnheader" key={`${head}-${index}`} className="text-center text-[12.5px] text-co-muted">
                  {head}
                </span>
              ))}
            </div>
            {Array.from({ length: weeks }, (_, week) => (
              <div role="row" key={week} className="contents">
                {cells.slice(week * 7, week * 7 + 7).map((cell) => {
                  const list = byDay.get(cell.key) ?? [];
                  const chosen = isOpenMonth && cell.inMonth && cell.day === chosenDay;
                  const isToday = cell.inMonth && cell.key === todayKey;
                  return (
                    <button
                      key={cell.key}
                      type="button"
                      role="gridcell"
                      disabled={!cell.inMonth}
                      aria-selected={chosen}
                      onClick={() => pick(cell.day)}
                      className={cn(
                        "flex h-[42px] flex-col items-center justify-center gap-[3px] outline-none",
                        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
                        chosen && "bg-co-hover",
                      )}
                    >
                      <span
                        className={cn(
                          "text-[15px] font-medium tabular-nums leading-none",
                          !cell.inMonth ? "text-co-faint" : isToday ? "text-co-accent-ink" : "text-co-text",
                        )}
                      >
                        {cell.day}
                      </span>
                      <PickerDot posts={list} />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1.5 shadow-[inset_0_1px_0_var(--co-line)]">
            <span className="flex-1 text-[12.5px] text-co-muted">Tap a day to jump the list.</span>
            <button
              type="button"
              onClick={() => {
                onClose();
                picker.onPick(today.getUTCFullYear(), today.getUTCMonth() + 1);
              }}
              className="inline-flex h-8 items-center text-[13px] font-semibold text-co-accent-ink outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
            >
              Today
            </button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

/** A day's dot: yours in the accent ink, the client's hollow, live green, the rest muted. */
function PickerDot({ posts }: { posts: CalendarPost[] }) {
  if (posts.length === 0) return <span aria-hidden="true" className="size-[5px]" />;
  if (posts.every((post) => post.posted)) return <span aria-hidden="true" className="size-[5px] rounded-full bg-co-live" />;
  if (posts.some((post) => post.group === "you")) {
    return <span aria-hidden="true" className="size-[5px] rounded-full bg-co-accent-ink" />;
  }
  if (posts.some((post) => post.group === "client")) {
    return <span aria-hidden="true" className="size-[5px] rounded-full shadow-[inset_0_0_0_1.2px_var(--co-text2)]" />;
  }
  return <span aria-hidden="true" className="size-[5px] rounded-full bg-co-muted" />;
}
